import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FullPageSpinner } from '@/components/etainabl-spinner';
import { useKbArticles, useKbCollection } from '@/queries/kb';
import { useIsAdmin } from '@/lib/auth';
import { KbRestrictedBadge } from './KbRestrictedBadge';
import { KbArticleList } from './KbArticleList';

const PAGE_SIZE = 50;

export function KbCollectionView({ slug }: { slug: string }) {
  const [q, setQ] = useState('');
  const [skip, setSkip] = useState(0);
  const isAdmin = useIsAdmin();
  const collection = useKbCollection(slug);
  const articles = useKbArticles(
    { collection: slug, limit: PAGE_SIZE, ...(skip ? { skip } : {}), ...(q.trim() ? { q: q.trim() } : {}) },
    { enabled: Boolean(collection.data) }
  );

  if (collection.isLoading) return <FullPageSpinner />;

  if (collection.error || !collection.data) {
    return (
      <div className="container mx-auto max-w-3xl p-6">
        <p className="text-sm text-muted-foreground mb-4">Collection not found.</p>
        <Link to="/kb">
          <Button variant="outline" size="sm">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Knowledgebase
          </Button>
        </Link>
      </div>
    );
  }

  const c = collection.data;
  return (
    <div className="container mx-auto max-w-3xl p-6 space-y-6">
      <Link to="/kb" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" />
        Knowledgebase
      </Link>

      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          {c.name}
          {isAdmin && <KbRestrictedBadge article={c} />}
        </h1>
        {c.description && <p className="text-muted-foreground">{c.description}</p>}
      </header>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={`Search ${c.name}…`}
          aria-label={`Search ${c.name}`}
          className="pl-9"
          value={q}
          onChange={e => {
            setQ(e.target.value);
            setSkip(0);
          }}
        />
      </div>

      <KbArticleList
        articles={articles.data?.data}
        isLoading={articles.isLoading}
        isAdmin={isAdmin}
        page={articles.data ? { total: articles.data.total, skip, limit: PAGE_SIZE, onSkip: setSkip } : undefined}
        emptyText={q.trim() ? `No articles in ${c.name} match "${q.trim()}".` : 'No articles in this collection yet.'}
      />
    </div>
  );
}
